import type { ReactNode } from 'react'
import { fbt } from 'fbtee'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DayTabsList } from '@/components/DayTabsList'
import { PillButton } from '@/components/PillButton'
import { Slider } from '@/components/ui/slider'
import type { WallpaperHighlight, WallpaperOptions as Options } from '@/lib/scheduleWallpaper'
import { cn } from '@/lib/utils'
import type { Stage } from '@/types'

// The lock-screen image's settings: which day, which of your picks stand out
// (the rest fade, like the schedule's filter), which stages, and which hours.
export function WallpaperOptions({ options, onChange, days, dayStages, dayRange, hourLabel }: {
  options: Options
  onChange: (next: Options) => void
  days: string[]
  /** Stages with sets on the selected day, in the user's order. */
  dayStages: Stage[]
  /** The selected day's hours, in festival minutes; null on a day with no timed sets. */
  dayRange: { start: number; end: number } | null
  /** A festival-minute time as the user's clock shows it. */
  hourLabel: (minutes: number) => string
}) {
  const set = (patch: Partial<Options>) => onChange({ ...options, ...patch })
  const toggleStage = (id: string) => {
    const hidden = new Set(options.hiddenStageIds)
    if (hidden.has(id)) hidden.delete(id)
    else hidden.add(id)
    set({ hiddenStageIds: hidden })
  }

  const highlights: [WallpaperHighlight, string][] = [
    ['off', fbt('None', 'Lock screen image option: a plain schedule with none of your picks marked')],
    ['none', fbt('All sets', 'Lock screen image option: no set is faded')],
    ['interested', fbt('Interested', 'Schedule filter chip label when showing sets you are interested in')],
    ['going', fbt('Going', 'Schedule filter chip label when showing only sets you are going to')],
  ]

  return (
    <div className="space-y-3">
      {days.length > 1 && (
        <Section label={fbt('Day', 'Lock screen image settings: section label for the day')}>
          <Tabs value={options.day} onValueChange={day => set({ day, range: undefined })}>
            <DayTabsList days={days} />
          </Tabs>
        </Section>
      )}
      <Section label={fbt('Highlight', 'Lock screen image settings: section label for which picks to highlight')}>
        <Tabs value={options.highlight} onValueChange={value => set({ highlight: value as WallpaperHighlight })}>
          <TabsList className="w-full">
            {highlights.map(([value, label]) => (
              <TabsTrigger key={value} value={value} className="flex-1">{label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </Section>
      {dayRange && dayRange.end - dayRange.start > 60 && (
        <HoursSlider range={options.range ?? dayRange} dayRange={dayRange} hourLabel={hourLabel} onChange={range => set({ range })} />
      )}
      {dayStages.length > 1 && (
        <Section label={fbt('Stages', 'Lock screen image settings: section label for which stages show')}>
          <div className="flex flex-wrap gap-1.5">
            {dayStages.map(stage => {
              const shown = !options.hiddenStageIds.has(stage.id)
              return (
                <PillButton
                  key={stage.id}
                  type="button"
                  className={cn('border px-2.5 text-xs', shown ? 'border-primary' : 'border-border')}
                  active={shown}
                  aria-pressed={shown}
                  onClick={() => toggleStage(stage.id)}
                >
                  {stage.name}
                </PillButton>
              )
            })}
          </div>
        </Section>
      )}
    </div>
  )
}

// Whole hours, so the image always starts and ends on a gridline. Picking the
// whole day stores no range, so switching days or a later set doesn't get cut.
function HoursSlider({ range, dayRange, hourLabel, onChange }: {
  range: { start: number; end: number }
  dayRange: { start: number; end: number }
  hourLabel: (minutes: number) => string
  onChange: (range: { start: number; end: number } | undefined) => void
}) {
  return (
    <Section
      label={fbt('Hours', 'Lock screen image settings: section label for the hours the image covers')}
      trailing={<span className="tabular-nums">{hourLabel(range.start)} – {hourLabel(range.end)}</span>}
    >
      <Slider
        className="px-1 py-2"
        min={dayRange.start}
        max={dayRange.end}
        step={60}
        minStepsBetweenThumbs={1}
        value={[range.start, range.end]}
        onValueChange={v => {
          const [start, end] = v as [number, number]
          onChange(start === dayRange.start && end === dayRange.end ? undefined : { start, end })
        }}
        aria-label={fbt('Hours on the image', 'Accessible label for the lock screen image hours slider')}
      />
    </Section>
  )
}

function Section({ label, trailing, children }: { label: string; trailing?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        <p>{label}</p>
        {trailing && <span className="normal-case tracking-normal text-foreground">{trailing}</span>}
      </div>
      {children}
    </div>
  )
}
